import React, { createContext, useContext, useEffect, useState } from "react";

// Lightweight i18n — extensible to any language. Keep keys flat & explicit.
const STRINGS = {
  en: {
    "nav.features": "Features",
    "nav.vet_care": "Vet Care",
    "nav.partners": "For Businesses",
    "nav.pricing": "Pricing",
    "nav.tour": "Take the tour",
    "nav.signin": "Get started",
    "nav.list_business": "Business",
    "hero.tagline": "India's #1 Pet Care Super-App",
    "hero.headline_a": "A healthier,",
    "hero.headline_em": "happier",
    "hero.headline_b": "life for your pet.",
    "hero.sub": "Vets in 60s. AI symptom check 24/7. Rescue, foster, sponsor. The whole pet world — one app.",
    "hero.cta_primary": "Start free — 7-day trial",
    "hero.cta_secondary": "See how it works",
    "trust.parents": "pet parents",
    "trust.vets": "verified vets",
    "trust.reunited": "pets reunited",
    "trust.donated": "NGO donations",
    "feat.section_eyebrow": "Built for every pet, every parent",
    "feat.section_heading": "Everything pets, in one place.",
    "rescue.eyebrow": "Rescue-to-Home Pipeline",
    "rescue.heading": "We connect every life that needs saving.",
    "rescue.sub": "Stray → NGO → Pro-bono vet → Foster home → Forever family. One unified timeline. Sponsors keep them alive monthly.",
    "ngo.eyebrow": "Court-Mandated Compliance",
    "ngo.heading": "ABC Rules 2023 register — built in.",
    "ngo.sub": "Tag, capture, sterilization, anti-rabies, microchip, release, audit-ready PDF. Inspection-day stress = zero.",
    "global.eyebrow": "Global growth-ready",
    "global.heading": "Built in India. Engineered for the world.",
    "global.sub": "Multi-currency, multi-language, regional payment gateways, dynamic admin config. Day-one ready for any market.",
    "footer.tagline": "Meet · Care · Adopt · Shop",
    "footer.made": "Made with ♥ in India",
  },
  hi: {
    "nav.features": "विशेषताएँ",
    "nav.vet_care": "पशु-चिकित्सा",
    "nav.partners": "व्यापार के लिए",
    "nav.pricing": "मूल्य निर्धारण",
    "nav.tour": "ऐप दिखाएँ",
    "nav.signin": "शुरू करें",
    "nav.list_business": "व्यवसाय",
    "hero.tagline": "भारत का #1 पेट सुपर-ऐप",
    "hero.headline_a": "एक स्वस्थ,",
    "hero.headline_em": "खुशहाल",
    "hero.headline_b": "जीवन आपके पालतू के लिए।",
    "hero.sub": "60 सेकंड में डॉक्टर। 24/7 AI लक्षण जाँच। बचाव, फॉस्टर, स्पॉन्सर। पूरा पेट संसार — एक ऐप में।",
    "hero.cta_primary": "मुफ़्त शुरू करें — 7 दिन",
    "hero.cta_secondary": "कैसे काम करता है देखें",
    "trust.parents": "पालतू माता-पिता",
    "trust.vets": "प्रमाणित डॉक्टर",
    "trust.reunited": "पालतू मिले",
    "trust.donated": "NGO दान",
    "feat.section_eyebrow": "हर पालतू, हर माता-पिता के लिए",
    "feat.section_heading": "सब कुछ पेट्स के लिए, एक जगह।",
    "rescue.eyebrow": "बचाव-से-घर पाइपलाइन",
    "rescue.heading": "हर जान को घर तक जोड़ते हैं।",
    "rescue.sub": "सड़क → NGO → मुफ़्त डॉक्टर → फॉस्टर घर → स्थायी परिवार। एक एकीकृत समयरेखा।",
    "ngo.eyebrow": "अदालत-अनिवार्य अनुपालन",
    "ngo.heading": "ABC नियम 2023 रजिस्टर — अंतर्निहित।",
    "ngo.sub": "टैग, कैप्चर, नसबंदी, रेबीज़, माइक्रोचिप, रिलीज़ — एक क्लिक PDF।",
    "global.eyebrow": "विश्व विस्तार-तैयार",
    "global.heading": "भारत में बना। दुनिया के लिए तैयार।",
    "global.sub": "बहु-मुद्रा, बहु-भाषा, क्षेत्रीय भुगतान। किसी भी बाज़ार के लिए तैयार।",
    "footer.tagline": "मिलें · देखभाल · गोद · खरीदें",
    "footer.made": "भारत में ♥ से बना",
  },
};

const I18nCtx = createContext({ t: (k) => k, lang: "en", setLang: () => {}, available: ["en", "hi"] });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem("petbond_lang") || "en"; } catch { return "en"; }
  });
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem("petbond_lang", l); } catch {}
  };
  const t = (key) => STRINGS[lang]?.[key] || STRINGS.en[key] || key;
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  return <I18nCtx.Provider value={{ t, lang, setLang, available: Object.keys(STRINGS) }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);

export function LanguageSwitcher({ className = "" }) {
  const { lang, setLang, available } = useI18n();
  const labels = { en: "EN", hi: "हिं" };
  return (
    <div data-testid="language-switcher" className={`inline-flex bg-surface-muted/70 rounded-full p-0.5 ${className}`}>
      {available.map(l => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${lang === l ? "bg-ink text-white" : "text-ink-soft hover:text-ink"}`}
        >
          {labels[l] || l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
